// ItemDiscounts.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Spin } from "antd";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import Swal from "sweetalert2";

import "jspdf-autotable";
import { PlusCircle } from "react-feather";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { RotateCcw } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import Axios from "axios";
import { API_BASE_URL, GET_USER_STORES } from "../../Config"; // ← your correct import path
import AddDiscount from "./AddDiscount";
import EditDiscount from "./EditDiscount";

const ItemDiscounts = () => {
  const [userData, setUserData] = useState(null);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]); // ← new: product dropdown options
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false); // loader for products

  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddDiscount, setShowAddDiscount] = useState(false);
  const [showEditDiscount, setShowEditDiscount] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => {
    const userStr = sessionStorage.getItem("userData");
    if (userStr) {
      setUserData(JSON.parse(userStr));
    }
  }, []);

  // 2. Load stores
  useEffect(() => {
    if (!userData?.OrgId || !userData?.UserId) return;

    Axios.get(
      `${API_BASE_URL}${GET_USER_STORES}?OrgId=${userData.OrgId}&UserId=${userData.UserId}`,
    )
      .then((res) => {
        if (res.data?.ResultData) {
          const storeOpts = [
            { value: 0, label: "All Stores" },
            ...res.data.ResultData.map((s) => ({
              value: s.StoreId || s.Id,
              label: s.Name,
            })),
          ];
          setStores(storeOpts);
        }
      })
      .catch((err) => console.error("Stores fetch failed:", err));
  }, [userData]);

  // 3. Load categories from sessionStorage (same as your other screens)
  useEffect(() => {
    const catStr = sessionStorage.getItem("filteredDDLData_CATEGORY");
    if (catStr) {
      const catData = JSON.parse(catStr);
      const catOpts = [
        { value: 0, label: "All Categories" },
        ...catData.map((c) => ({
          value: c.ItemId,
          label: c.ItemValue || c.DisplayValue,
        })),
      ];
      setCategories(catOpts);
    }
  }, []);

  // 4. Load products when store or category changes (same pattern as AddPurchases)
  useEffect(() => {
    if (!userData?.OrgId) {
      setProducts([]);
      return;
    }

    const storeId = selectedStore?.value || 0;

    const fetchProducts = async () => {
      setProductLoading(true);
      try {
        const params = {
          OrgId: userData.OrgId,
          StoreId: storeId,
          CategoryId: selectedCategory?.value || 0,
          ProductId: 0,
        };

        const res = await Axios.get(`${API_BASE_URL}Product/ProductsDDL`, {
          params,
        });

        if (res.data?.ResultData) {
          setProducts(res.data.ResultData);
          console.log("Products loaded:", res.data.ResultData.length);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Products fetch failed:", err);
        setProducts([]);
      } finally {
        setProductLoading(false);
      }
    };

    fetchProducts();
  }, [userData, selectedStore?.value, selectedCategory?.value]);

  // 5. Fetch discounts when filters change
  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const params = {
        OrgId: userData.OrgId,
        StoreId: selectedStore?.value || 0,
        ProductId: selectedProduct?.value || 0, // optional product filter
      };

      const res = await Axios.get(
        `${API_BASE_URL}Settings/GetStoreProdDiscount`,
        { params },
      );

      if (res.data?.Status && res.data?.ResultData) {
        setDiscounts(res.data.ResultData);
      } else {
        setDiscounts([]);
      }
    } catch (err) {
      console.error("Discounts fetch failed:", err);
      setDiscounts([]);
      Swal.fire("Error", "Failed to load discounts", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userData?.OrgId) return;
    fetchDiscounts();
  }, [userData, selectedStore, selectedProduct]); // ← category not needed here (optional)

  // Table columns (same as before)
  const columns = [
    {
      title: " Name",
      dataIndex: "DiscountName",
      sorter: (a, b) => a.DiscountName?.localeCompare(b.DiscountName),
    },
    {
      title: "Product",
      dataIndex: "ProductName",
      sorter: (a, b) => a.ProductName?.localeCompare(b.ProductName),
    },
    // { title: "BarCode", dataIndex: "BarCode" },
    { title: "Qty", dataIndex: "Quantity", render: (qty) => qty || "-" },
    {
      title: "From",
      dataIndex: "FromDate",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-GB") : "-",
      sorter: (a, b) => new Date(a.FromDate) - new Date(b.FromDate),
    },
    {
      title: "To",
      dataIndex: "ToDate",
      render: (date) =>
        date ? new Date(date).toLocaleDateString("en-GB") : "-",
      sorter: (a, b) => new Date(a.ToDate) - new Date(b.ToDate),
    },
    // {
    //   title: "Discount Type",
    //   dataIndex: "IsPercentage",
    //   render: (isPct) => (isPct ? "Percentage" : "Fixed"),
    // },
    {
      title: "Value",
      dataIndex: "Value",
      render: (val, rec) => (rec.IsPercentage ? `${val}%` : val || "-"),
    },
    // {
    //   title: "Min Product Value",
    //   dataIndex: "MinProductValue",
    //   render: (val) => val || "-",
    // },
    {
      title: "Max Discount",
      dataIndex: "MaxDiscountValue",
      render: (val) => val || "-",
    },
    // {
    //   title: "Status",
    //   dataIndex: "IsActive",
    //   render: (active) => (
    //     <span className={`badge bg-${active ? "success" : "danger"}`}>
    //       {active ? "Active" : "Inactive"}
    //     </span>
    //   ),
    // },
    {
      title: "Action",
      key: "action",
      width: 120,
    render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2 mb-0"
              onClick={() => {
                setEditRecord(record);
                setShowEditDiscount(true);
              }}
            >
              <i data-feather="edit" className="feather-edit" />
            </Link>
            {/* <Link
               className="me-2 p-2 mb-0"
               data-bs-toggle="offcanvas"
               data-bs-target="#offcanvasEditExpense"
             >
               <i data-feather="edit" className="feather-edit" />
             </Link> */}
            <Link className="confirm-text p-2 mb-0">
              <i data-feather="trash-2" className="feather-trash-2" />
            </Link>
          </div>
        </td>
      ),
    },
  ];

  // PDF & Excel exports (same as before)
  const exportToPDF = () => {
    /* ... same as your previous code ... */
  };
  const exportToExcel = () => {
    /* ... same as your previous code ... */
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Item Discounts</h4>
              <h6>Manage product discounts </h6>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={<Tooltip>Pdf</Tooltip>}>
                <Link onClick={exportToPDF}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="pdf" />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Excel</Tooltip>}
              >
                <Link onClick={exportToExcel}>
                  <ImageWithBasePath
                    src="assets/img/icons/excel.svg"
                    alt="excel"
                  />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Refresh</Tooltip>}
              >
                <Link onClick={() => window.location.reload()}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            <button
              className="btn btn-added"
              onClick={() => setShowAddDiscount(true)}
            >
              <PlusCircle className="me-2" />
            </button>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            {/* Filters */}
            <div className="table-top ">
              <div className="d-flex align-items-end flex-wrap gap-3">
                <div style={{ width: 200 }}>
                  <label className="form-label mb-1">
                    Store <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Store"
                    value={selectedStore}
                    onChange={setSelectedStore}
                    options={stores}
                    allowClear
                  />
                </div>

                <div style={{ width: 200 }}>
                  <label className="form-label mb-1">Category</label>
                  <Select
                    placeholder="All Categories"
                    value={selectedCategory}
                    onChange={setSelectedCategory}
                    options={categories}
                    allowClear
                  />
                </div>

                <div style={{ width: 250 }}>
                  <label className="form-label mb-1">Product</label>
                  <Select
                    placeholder={
                      productLoading ? "Loading..." : "Select Product"
                    }
                    value={selectedProduct}
                    onChange={setSelectedProduct}
                    options={products.map((p) => ({
                      value: p.Id,
                      label: p.ProductName,
                    }))}
                    loading={productLoading}
                    allowClear
                    disabled={!selectedStore} // optional: disable if no store selected
                  />
                </div>
              </div>
            </div>

            {/* Table / Loader */}
            {loading ? (
              <div className="text-center my-5">
                <Spin size="large" tip="Loading discounts..." />
              </div>
            ) : discounts.length > 0 ? (
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={discounts}
                  rowKey="DiscountId"
                  pagination={{ pageSize: 10 }}
                />
              </div>
            ) : (
              <div className="text-center my-5">
                <p className="text-muted">
                  No discounts found for the selected filters.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddDiscount
        isOpen={showAddDiscount}
        onClose={() => setShowAddDiscount(false)}
        handleDiscountsView={() => {
          fetchDiscounts();
        }}
      />

     <EditDiscount
        isOpen={showEditDiscount}
        onClose={() => setShowEditDiscount(false)}
        handleDiscountsView={fetchDiscounts}  
        record={editRecord}                    
      />
    </div>
  );
};

export default ItemDiscounts;
